/**
 * Masonry grid layout helper.
 *
 * Used by the gallery layout composable to arrange variant cards of
 * different sizes into balanced columns, like a Pinterest-style board.
 *
 * The one thing to know: it works in two passes. Pass 1 decides which
 * column each item goes in (always the shortest column so far). Pass 2
 * turns those column assignments into real x/y pixel positions.
 */

export const GAP = 150;

/**
 * Two-pass masonry:
 *   Pass 1: assign each item to the currently shortest column,
 *           bookkeeping running heights.
 *   Pass 2: compute per-column widths (max item width in that column),
 *           then place items vertically inside each column.
 */
export function masonryLayout(
  items: { id: string; width: number; height: number }[],
  numCols: number,
  gap: number
) {
  if (items.length === 0)
    return {
      positions: new Map<string, { x: number; y: number }>(),
      totalWidth: 0,
      totalHeight: 0
    };

  const cols = numCols;
  const colHeights: number[] = Array.from({ length: cols }, () => 0);
  const colItems: {
    id: string;
    width: number;
    height: number;
    col: number;
  }[] = [];

  /**
   * Sort by area DESC so large items get placed first — gives the
   * best chance at a balanced column layout.
   */
  const sorted = [...items].toSorted(
    (a, b) => b.width * b.height - a.width * a.height
  );

  for (const item of sorted) {
    let minCol = 0;
    for (let c = 1; c < cols; c++) {
      if (colHeights[c]! < colHeights[minCol]!) minCol = c;
    }

    colItems.push({ ...item, col: minCol });
    colHeights[minCol]! += item.height + gap;
  }

  // Each column is as wide as the widest item in it.
  const colWidths: number[] = Array.from({ length: cols }, () => 0);
  for (const item of colItems) {
    colWidths[item.col] = Math.max(colWidths[item.col]!, item.width);
  }

  const colX: number[] = Array.from({ length: cols }, () => 0);
  for (let c = 1; c < cols; c++) {
    colX[c] = colX[c - 1]! + colWidths[c - 1]! + gap;
  }

  const colCurrentY: number[] = Array.from({ length: cols }, () => 0);
  const positions = new Map<string, { x: number; y: number }>();
  let totalWidth = 0;
  let totalHeight = 0;

  /**
   * Place in the same sorted order so vertical stacking matches the
   * assignment order from pass 1.
   */
  for (const item of colItems) {
    const x = colX[item.col]!;
    const y = colCurrentY[item.col]!;
    positions.set(item.id, { x, y });

    colCurrentY[item.col] = y + item.height + gap;
    totalWidth = Math.max(totalWidth, x + item.width);
    totalHeight = Math.max(totalHeight, y + item.height);
  }

  return { positions, totalWidth, totalHeight };
}
